import { useToken } from '@ori/auth';
import { useServiceUrls } from '@ori/configuration-manager';
import { useEnvironmentConfiguration } from '@ori/environment';
import { fetchGraphql, getHeaders } from '@ori/fetching';
import { useTranslationsWithNamespaces } from '@ori/i18n';
import { useGetTenantBackInStockNotificationsConfiguration } from '@ori/static-api-client-react';
import type { FC, PropsWithChildren } from 'react';
import React, { createContext, useCallback, useContext, useMemo } from 'react';

import { PACKAGE_NAME } from '../constants';
import { useDialogProps } from '../hooks';
import type {
  BackInStockNotificationSubscriptionInput,
  SubscribeToBackInStockMutation,
  SubscribeToBackInStockMutationVariables,
} from '../models';
import { subscribeToBackInStockNotification } from '../queries';
import type { ContextValues } from './types';

/* istanbul ignore next */
const defaultSubscribe = async () => ({});
/* istanbul ignore next */
const defaultClose = () => undefined;

export const AppContext = createContext<ContextValues>({
  config: undefined,
  loading: true,
  open: false,
  productCode: '',
  close: defaultClose,
  subscribe: defaultSubscribe,
  successClose: defaultClose,
});

export const AppContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { graphQlUrl } = useServiceUrls(['graphQl']);
  const { customerId, token } = useToken();
  const { tenant = '' } = useEnvironmentConfiguration();
  const { language } = useTranslationsWithNamespaces();
  const { close, open, productCode, successClose } = useDialogProps();
  const subscribe = useCallback(
    async (input: BackInStockNotificationSubscriptionInput) =>
      fetchGraphql<SubscribeToBackInStockMutation, SubscribeToBackInStockMutationVariables>({
        query: subscribeToBackInStockNotification,
        url: graphQlUrl,
        headers: getHeaders({
          customerId,
          language,
          sourceAppName: PACKAGE_NAME,
          tenant,
          token,
        }),
        variables: {
          input,
        },
      }),
    [customerId, graphQlUrl, language, tenant, token],
  );
  const { data, error } = useGetTenantBackInStockNotificationsConfiguration(
    tenant,
    { lang: language },
    {
      swr: {
        enabled: open,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        revalidateIfStale: false,
      },
    },
  );
  const loading = useMemo(() => data === undefined && error === undefined, [data, error]);

  return (
    <AppContext.Provider
      value={useMemo<ContextValues>(
        () => ({
          config: data,
          loading,
          open,
          productCode,
          close,
          subscribe,
          successClose,
        }),
        [close, data, loading, open, productCode, subscribe, successClose],
      )}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);
